import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { LeadsBoardResult } from "@/components/lead-pipeline/leadsApiMappers";
import {
  DEFAULT_LEAD_FILTERS,
  DEFAULT_LEAD_RANGE,
  type LeadColumn,
  type LeadFilters,
  type LeadRange,
  type LeadStats,
} from "@/components/lead-pipeline/types";

/**
 * Lead Pipeline state (Figma 4.1A) — the Kanban columns + derived KPI stats,
 * hydrated from `getLeadsBoardSSR`. `search` re-reads the board.
 */
export interface LeadPipelineState {
  columns: LeadColumn[];
  stats: LeadStats | null;
  search: string;
  filters: LeadFilters;
  range: LeadRange;
  loading: boolean;
  refetching: boolean;
}

const initialState: LeadPipelineState = {
  columns: [],
  stats: null,
  search: "",
  filters: DEFAULT_LEAD_FILTERS,
  range: DEFAULT_LEAD_RANGE,
  loading: true,
  refetching: false,
};

const leadPipelineSlice = createSlice({
  name: "leadPipeline",
  initialState,
  reducers: {
    hydrateLeadPipeline(state, action: PayloadAction<LeadsBoardResult>) {
      if (!action.payload) return;
      state.columns = action.payload.columns ?? [];
      state.stats = action.payload.stats ?? null;
      // The SSR board is always the unfiltered, default-range view (page.tsx
      // loads it with no search + DEFAULT_LEAD_RANGE). Redux persists across
      // client navigation, so reset the transient toolbar controls on every
      // page (re)entry — otherwise search/filters/range keep stale values that
      // no longer describe the freshly-loaded board.
      state.search = "";
      state.filters = DEFAULT_LEAD_FILTERS;
      state.range = DEFAULT_LEAD_RANGE;
    },
    setLeadsBoard(state, action: PayloadAction<LeadsBoardResult>) {
      if (!action.payload) return;
      state.columns = action.payload.columns ?? [];
      state.stats = action.payload.stats ?? null;
    },
    setLeadSearch(state, action: PayloadAction<string>) {
      state.search = action.payload;
    },
    setLeadFilters(state, action: PayloadAction<LeadFilters>) {
      state.filters = action.payload;
    },
    resetLeadFilters(state) {
      state.filters = DEFAULT_LEAD_FILTERS;
    },
    setLeadRange(state, action: PayloadAction<LeadRange>) {
      state.range = action.payload;
    },
    setLeadPipelineLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setLeadPipelineRefetching(state, action: PayloadAction<boolean>) {
      state.refetching = action.payload;
    },
  },
});

export const {
  hydrateLeadPipeline,
  setLeadsBoard,
  setLeadSearch,
  setLeadFilters,
  resetLeadFilters,
  setLeadRange,
  setLeadPipelineLoading,
  setLeadPipelineRefetching,
} = leadPipelineSlice.actions;

export default leadPipelineSlice.reducer;
